import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Table, { TableColumn } from '../../../../components/organisms/table';
import MVCHeader from '../../../../components/organisms/mvc-header';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import Actions from '../../../../components/molecules/actions';
import { ModalProps } from '../../../../components/template/mvc-modal';
import classNames from 'classnames';
import Text from '../../../../components/atoms/text';
import ContactsModal from './modal';
import ContactService, {
	IContact,
	IContatoAdministrativo,
} from '../../../../services/Contact.service';
import UserService, { IUser } from '../../../../services/User.service';
import ModalConfirmation from '../../../../components/organisms/modal-confirmation';
import SVGIcon from '../../../../components/atoms/icons';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import { useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

const initialState: IUser = {
	name: '',
	email: '',
	cpf: '',
	phone: '',
	role: '',
	client: {},
	yards: [],
	isActive: true,
	shouldReceiveAlerts: false,
	acessoFluxoUnico: false,
	permissions: {
		isMaintenance: false,
		isMonitoring: false,
		isMovement: false,
		isReport: false,
		isLocalAdmin: false,
		isSystemAdmin: false,
	},
};

const Contacts = () => {
	const { t } = useTranslation();

	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const modalTitles = {
		create: t('admin.contacts.new'),
		edit: t('admin.contacts.edit'),
		view: t('admin.contacts.view'),
	};

	const [data, setData] = useState<IContatoAdministrativo[]>([]);
	const [isDeleteModalVisible, setIsDeleteModalVisible] =
		useState<boolean>(false);
	const [dataToDelete, setDataToDelete] =
		useState<IContatoAdministrativo | null>(null);

	const [paginationOptions, setPaginationOptions] = useState<PaginationProps>(
		{
			pageCurrent: 1,
			pageSize: 10,
			totalItems: 0,
			totalPages: 1,
		}
	);

	const userType = {
		User: t('admin.user'),
		Client: t('admin.client_contact'),
		Shipowner: t('port_management.ships.owner'),
	};

	const [modalProps, setModalProps] = useState<ModalProps<IContatoAdministrativo>>({
		data: initialState,
		title: modalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: initialState,
			}));
			if (reload) fetchData();
		},
	});

	const fetchData = useCallback(async () => {
		const response = await ContactService.listarComUsuarios({
			pageCurrent: paginationOptions.pageCurrent,
			pageSize: paginationOptions.pageSize,
			account: 'Contact',
			idClient: Number(accountId),
		});
		if (response) {
			if (paginationOptions.pageCurrent > response.totalPages) {
				setPaginationOptions((prev) => ({
					...prev,
					pageCurrent: response.totalPages,
				}));
				return;
			}
			setData(response.data);
			setPaginationOptions((prev) => ({
				...prev,
				totalItems: response.totalItems,
				totalPages: response.totalPages,
			}));
		}
	}, [paginationOptions.pageCurrent, paginationOptions.pageSize, accountId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const onPaginationUpdate = (pageSize: number, pageCurrent: number) => {
		setPaginationOptions((prev) => ({ ...prev, pageSize, pageCurrent }));
	};

	const onActionsViewClick = useCallback(
		(data: IContatoAdministrativo) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.view,
				mode: 'view',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.view]
	);

	const onActionsEditClick = useCallback(
		(data: IContatoAdministrativo) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.edit,
				mode: 'edit',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.edit]
	);

	const onCreateClick = useCallback(() => {
		setModalProps((prev) => ({
			...prev,
			title: modalTitles.create,
			mode: 'create',
			isModalVisible: true,
			data: initialState,
		}));
	}, [modalTitles.create]);

	const onActionsDeleteClick = (data: IContatoAdministrativo) => {
		setDataToDelete(data);
		setIsDeleteModalVisible(true);
	};

	const onDeleteModalClose = () => {
		setIsDeleteModalVisible(false);
		setDataToDelete(null);
	};

	const onDeleteConfirm = async () => {
		if (!dataToDelete || !dataToDelete.id) return;
		if (dataToDelete.origem === 'contacts') {
			await ContactService.delete(dataToDelete.id);
		} else {
			await UserService.delete(dataToDelete.id);
		}
		fetchData();
		NotificationToast.Success(
			`${t('admin.contact')} ${t('alert.success.delete')}`
		);
		onDeleteModalClose();
	};

	const getYards = (obj: IContact): string => {
		const yards = obj.yards?.map((item) => item.name);
		return yards?.join(', ');
	};

	const getCarriers = (obj: IContact): string => {
		const carriers = obj.carriers?.map((item) => item.name);
		return carriers?.join(', ');
	};

	const columns: TableColumn<IContatoAdministrativo>[] = [
		{ key: 'name', label: t('misc.name') },
		{ key: 'email', label: t('misc.email') },
		{ key: 'phone', label: t('misc.phone') },
		{
			key: 'client',
			label: t('admin.contacts.account'),
			children: (row) => (
				<Text className="text-xs">{row.client?.fantasyName}</Text>
			),
		},
		{
			key: 'type',
			label: t('admin.contacts.type'),
			children: (row) => (
				<Text className="text-xs">
					{userType[row.type as keyof typeof userType] ?? row.type}
				</Text>
			),
		},
		{
			key: 'carriers',
			label: t('port_management.carriers.title'),
			children: (row) => (
				<Text className="text-xs">
					{row.carriers?.map((carrier) => carrier.name).join(', ')}
				</Text>
			),
		},
		{
			key: 'actions',
			label: t('misc.actions'),
			children: (row) => (
				<Actions
					onViewClick={() => onActionsViewClick(row)}
					onEditClick={() => onActionsEditClick(row)}
					onDeleteClick={() => onActionsDeleteClick(row)}
				/>
			),
		},
	];

	return (
		<>
			<div className="flex-grow grid grid-rows-[auto_1fr_auto] p-4 gap-y-4">
				<MVCHeader
					title={t('admin.contacts.list')}
					onCreateText={t('admin.contacts.new')}
					onCreateClick={onCreateClick}
				/>

				<div className="overflow-x-auto w-full">
					<Table
						data={data}
						columns={columns}
						className={classNames(
							'grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]'
						)}
					/>
				</div>

				<Pagination
					{...paginationOptions}
					onUpdate={onPaginationUpdate}
				/>
			</div>

			<ContactsModal setModalProps={setModalProps} {...modalProps} />
			<ModalConfirmation
				onConfirm={onDeleteConfirm}
				onCancel={onDeleteModalClose}
				isModalVisible={isDeleteModalVisible}
			>
				<div className="flex flex-col items-center text-center gap-4">
					<SVGIcon
						icon="Error"
						className="h-12 w-auto fill-red stroke-white"
					/>
					<Text>
						{t('alert.confirmation.delete')}
						<b> {dataToDelete?.name}</b>?
					</Text>
				</div>
			</ModalConfirmation>
		</>
	);
};

export default Contacts;
